import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../axios/axiosInstance";
import PermissionGuard from "../../../components/PermissionGuard";
import * as XLSX from "xlsx";

const ZoneList = () => {
  const navigate = useNavigate();
  
  // Check if user is Super Admin
  const role = Number(localStorage.getItem("role"));
  const isSuperAdmin = role === 0 || localStorage.getItem("isSuperAdmin") === "true";

  const [loading, setLoading] = useState(false);
  const [zones, setZones] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [usersForZoneAdmin, setUsersForZoneAdmin] = useState<any[]>([]);
  const [memberTypes, setMemberTypes] = useState<Array<{ label: string; value: string }>>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [formData, setFormData] = useState<any>({});
  const [openFormModal, setOpenFormModal] = useState(false);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [viewZoneModal, setViewZoneModal] = useState(false);
  const [viewingZone, setViewingZone] = useState<any>(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedFields, setSelectedFields] = useState<Record<string, boolean>>({
    zoneName: true,
    memberName: true,
    phone: true,
    city: true,
  });
  
  const [zoneAdminSearchQuery, setZoneAdminSearchQuery] = useState("");
  const [zoneAdminMemberTypeFilter, setZoneAdminMemberTypeFilter] = useState<string>("");
  const [showZoneAdminDropdown, setShowZoneAdminDropdown] = useState(false);
  const zoneAdminDropdownRef = useRef<HTMLDivElement>(null);
  
  // Member selection states
  const [allMembers, setAllMembers] = useState<any[]>([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [memberTypeFilter, setMemberTypeFilter] = useState<string>("");
  const [showMemberModal, setShowMemberModal] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const deleteModalRef = useRef<HTMLDivElement>(null);
  
  // Locality (City) states
  const [cities, setCities] = useState<any[]>([]);
  const [selectedCityIds, setSelectedCityIds] = useState<number[]>([]);
  const [citySearchQuery, setCitySearchQuery] = useState("");
  const [showCityModal, setShowCityModal] = useState(false);
  const [zoneFilter, setZoneFilter] = useState<string>("");
  
  // Location filter states for city selection
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);
  const [taluks, setTaluks] = useState<any[]>([]);
  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(null);
  const [selectedStateId, setSelectedStateId] = useState<number | null>(null);
  const [selectedDistrictId, setSelectedDistrictId] = useState<number | null>(null);
  const [selectedTalukId, setSelectedTalukId] = useState<number | null>(null);
  const [useCityFilter, setUseCityFilter] = useState<boolean>(false);

  useEffect(() => {
    fetchZones();
    fetchMembers();
    fetchUsersForZoneAdmin();
    fetchMemberTypes();
    fetchAllCities();
    fetchCountries();
    fetchAllMembers();
  }, []);

  const fetchAllMembers = async () => {
    try {
      const res: any = await axiosInstance.get(`/api/user?limit=1000`);
      if (res.status === 200) {
        // Handle nested response structure: res.data.data (pagination wrapper)
        const responseData = res.data?.data;
        // Check if it's paginated response with data array
        const membersData = Array.isArray(responseData?.data) 
          ? responseData.data 
          : Array.isArray(responseData) 
            ? responseData 
            : [];
        setAllMembers(membersData);
      } else {
        setAllMembers([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch all members:', error);
      toast.error("Failed to fetch members");
      setAllMembers([]);
    }
  };

  const fetchAllCities = async () => {
    try {
      const res = await axiosInstance.get<{ status: string; data: any[] }>(`/api/city`);
      if (res.status === 200) {
        setCities(res.data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch cities:', error);
      toast.error("Failed to fetch localities");
    }
  };

  const fetchCities = async (talukId: number | null) => {
    if (!talukId) {
      setCities([]);
      return;
    }
    try {
      const res = await axiosInstance.get<{ status: string; data: any[] }>(`/api/city?taluk_id=${talukId}`);
      if (res.status === 200) {
        setCities(res.data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch cities:', error);
      setCities([]);
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await axiosInstance.get<{ status: string; data: any[] }>('/api/country');
      if (res.status === 200) {
        setCountries(res.data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchStates = async (countryId: number | null) => {
    if (!countryId) {
      setStates([]);
      return;
    }
    try {
      const res = await axiosInstance.get<{ status: string; data: any[] }>(`/api/state?country_id=${countryId}`);
      if (res.status === 200) {
        setStates(res.data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch states:', error);
      setStates([]);
    }
  };

  const fetchDistricts = async (stateId: number | null) => {
    if (!stateId) {
      setDistricts([]);
      return;
    }
    try {
      const res = await axiosInstance.get<{ status: string; data: any[] }>(`/api/district?state_id=${stateId}`);
      if (res.status === 200) {
        setDistricts(res.data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch districts:', error);
      setDistricts([]);
    }
  };

  const fetchTaluks = async (districtId: number | null) => {
    if (!districtId) {
      setTaluks([]);
      return;
    }
    try {
      const res = await axiosInstance.get<{ status: string; data: any[] }>(`/api/taluk?district_id=${districtId}`);
      if (res.status === 200) {
        setTaluks(res.data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch taluks:', error);
      setTaluks([]);
    }
  };

  const fetchZones = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get<{ data: any[] }>(`/api/zone/all`);
      setZones(res.data?.data || []);
    } catch (err) {
      toast.error("Failed to fetch zones");
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const res = await axiosInstance.get<{ data: any[] }>(`/api/zone/members`);
      setMembers(res.data?.data || []);
    } catch (err) {
      toast.error("Failed to fetch members");
    }
  };

  const fetchUsersForZoneAdmin = async () => {
    try {
      const res = await axiosInstance.get<{ data: any[] }>(`/api/zone/users-for-admin`);
      setUsersForZoneAdmin(res.data?.data || []);
    } catch (err) {
      toast.error("Failed to fetch users for zone admin");
    }
  };

  const fetchMemberTypes = async () => {
    try {
      const resp: any = await axiosInstance.get("/api/member-type");
      if (resp.status === 200 && resp.data?.status === "ok") {
        const types = resp.data.data.map((type: any) => ({
          label: type.label,
          value: type.value,
        }));
        setMemberTypes(types);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch member types");
      setMemberTypes([]);
    }
  };

  const handleFormOpen = (data: any = {}) => {
    console.log("Edit Id", data.id);
    setFormData({
      id: data.id || null,
      zone_name: data.zone_name || "",
      area: data.zone_area || "",
      leader: data?.leader?.id || null,
      zone_admin_ids: data?.zone_admins?.map((admin: any) => admin.id) || [],
      city_ids: data?.cities?.map((city: any) => city.id) || [],
      member_ids: data?.users?.map((user: any) => user.id) || [],
    });
    setSelectedCityIds(data?.cities?.map((city: any) => city.id) || []);
    setSelectedMemberIds(data?.users?.map((user: any) => user.id) || []);
    setOpenFormModal(true);
  };

  const handleFormClose = () => {
    setFormData({});
    setSelectedCityIds([]);
    setSelectedMemberIds([]);
    setCitySearchQuery("");
    setMemberSearchQuery("");
    setMemberTypeFilter("");
    setZoneFilter("");
    setUseCityFilter(false);
    setSelectedCountryId(null);
    setSelectedStateId(null);
    setSelectedDistrictId(null);
    setSelectedTalukId(null);
    setStates([]);
    setDistricts([]);
    setTaluks([]);
    setShowMemberModal(false);
    fetchAllCities();
    setOpenFormModal(false);
  };

  const handleDeleteOpen = (id: number) => {
    setDeleteId(id);
    setOpenDeleteModal(true);
  };

  const handleDeleteClose = () => {
    setDeleteId(null);
    setOpenDeleteModal(false);
  };

  const handleViewZone = (zone: any) => {
    setViewingZone(zone);
    setViewZoneModal(true);
  };

  const handleCloseViewModal = () => {
    setViewZoneModal(false);
    setViewingZone(null);
  };

  const handleDownloadExcel = () => {
    try {
      // Use the selected zone from viewingZone, or all zones if viewingZone is null
      const zonesToExport = viewingZone ? [viewingZone] : zones;
      
      // Collect zone members data
      const exportData: any[] = [];
      
      zonesToExport.forEach((zone) => {
        if (zone.users && zone.users.length > 0) {
          zone.users.forEach((member: any) => {
            const row: any = {};
            
            if (selectedFields.zoneName) row["Zone Name"] = zone.zone_name || "";
            if (selectedFields.memberName) row["Member Name"] = member.name || "";
            if (selectedFields.phone) row["Phone"] = member.phone || "";
            if (selectedFields.city) row["Locality"] = member.city?.name || "";
            
            exportData.push(row);
          });
        }
      });

      if (exportData.length === 0) {
        toast.error("No data to export");
        return;
      }

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Zone Members");

      // Generate filename with timestamp and zone name if single zone
      const timestamp = new Date().toISOString().split('T')[0];
      const zoneName = viewingZone ? `_${viewingZone.zone_name?.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
      const filename = `zone_members${zoneName}_${timestamp}.xlsx`;

      // Download the file
      XLSX.writeFile(wb, filename);
      toast.success("Excel file downloaded successfully!");
    } catch (error) {
      console.error("Error downloading Excel:", error);
      toast.error("Failed to download Excel file");
    }
  };

  const handleFormChange = (key: string, value: any) => {
    setFormData({ ...formData, [key]: value });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (zoneAdminDropdownRef.current && !zoneAdminDropdownRef.current.contains(event.target as Node)) {
        setShowZoneAdminDropdown(false);
      }
    };

    if (showZoneAdminDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showZoneAdminDropdown]);


  const filteredUsersForZoneAdmin = usersForZoneAdmin.filter((user) => {
    const matchesSearch = !zoneAdminSearchQuery || user?.name?.toLowerCase().includes(zoneAdminSearchQuery.toLowerCase());
    const matchesType = !zoneAdminMemberTypeFilter || user?.member_type === zoneAdminMemberTypeFilter;
    return matchesSearch && matchesType;
  });

  const selectedZoneAdmins = usersForZoneAdmin.filter((user) =>
    formData?.zone_admin_ids?.includes(user.id)
  );

  const handleZoneAdminToggle = (userId: number) => {
    const currentIds = formData?.zone_admin_ids || [];
    if (currentIds.includes(userId)) {
      handleFormChange("zone_admin_ids", currentIds.filter((id: number) => id !== userId));
    } else {
      handleFormChange("zone_admin_ids", [...currentIds, userId]);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData?.zone_name) {
        toast.error("Zone name is required");
        return;
      }
      const payload = {
        name: formData.zone_name,
        area: formData.area || "",
        leader: formData.leader || null,
        zone_admin_ids: formData.zone_admin_ids || [],
        city_ids: selectedCityIds || [],
        member_ids: selectedMemberIds || [],
      };
      console.log("Payload", payload);
      if (formData?.id && zones.find((z) => z.id === formData.id)) {
        // UPDATE
        const response = await axiosInstance.put(
          `/api/zone/${formData.id}`,
          payload
        );

        if (response.status === 200) {
          const responseData = response.data as {
            status: string;
            message?: string;
            data?: any;
          };
          if (responseData?.status === "ok") {
            // Check if it's a direct execution or a request
            if (responseData?.message?.includes("pending approval")) {
              toast.success("Zone update request sent to Super Admin");
            } else {
              toast.success("Zone updated successfully");
            }
          } else {
            const errMsg = responseData?.message || "Update failed";
            toast.error(errMsg);
          }
        } else {
          toast.error(`Unexpected response: ${response.status}`);
        }
      } else {
        // CREATE
        const response = await axiosInstance.post(`/api/zone`, payload);
        if (response.status === 200) {
          const responseData = response.data as {
            status: string;
            message?: string;
            data?: any;
          };
          // Check if it's a direct execution or a request
          if (responseData?.message?.includes("pending approval")) {
            toast.success("Zone creation request sent to Super Admin");
          } else {
            toast.success("Zone created successfully");
          }
        }
      }
      handleFormClose();
      fetchZones();
    } catch (err) {
      console.error(err);
      toast.error("Error saving zone");
    }
  };

  const handleDelete = async () => {
    try {
      if (deleteId) {
        const response = await axiosInstance.delete(`/api/zone/${deleteId}`);
        if (response.status === 200) {
          const responseData = response.data as {
            status: string;
            message?: string;
            data?: any;
          };
          // Check if it's a direct execution or a request
          if (responseData?.message?.includes("pending approval")) {
            toast.success("Zone deletion request sent to Super Admin");
          } else {
            toast.success("Zone deleted successfully");
          }
          fetchZones();
          handleDeleteClose();
        }
      }
    } catch (err: any) {
      // Extract error message from backend response
      const errorMessage = err?.response?.data?.body || 
                          err?.response?.data?.message || 
                          err?.message || 
                          "Error deleting zone";
      toast.error(errorMessage);
    }
  };

  const filteredZones = zones.filter(
    (zone) => {
      const matchesSearch = !searchQuery ||
        zone.zone_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        zone.zone_area?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        zone?.leader?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        zone?.zone_admins?.some((admin: any) => 
          admin?.name?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      
      const matchesZoneFilter = !zoneFilter || zone.id === Number(zoneFilter);
      
      return matchesSearch && matchesZoneFilter;
    }
  );

  const filteredCities = cities.filter((city) =>
    !citySearchQuery ||
    city.name?.toLowerCase().includes(citySearchQuery.toLowerCase()) ||
    city.code?.toLowerCase().includes(citySearchQuery.toLowerCase())
  );

  // Handle location cascading for city filter
  useEffect(() => {
    if (selectedCountryId) {
      fetchStates(selectedCountryId);
    } else {
      setStates([]);
      setDistricts([]);
      setTaluks([]);
      setCities([]);
    }
  }, [selectedCountryId]);

  useEffect(() => {
    if (selectedStateId) {
      fetchDistricts(selectedStateId);
    } else {
      setDistricts([]);
      setTaluks([]);
      setCities([]);
    }
  }, [selectedStateId]);

  useEffect(() => {
    if (selectedDistrictId) {
      fetchTaluks(selectedDistrictId);
    } else {
      setTaluks([]);
      setCities([]);
    }
  }, [selectedDistrictId]);

  useEffect(() => {
    if (useCityFilter) {
      if (selectedTalukId) {
        fetchCities(selectedTalukId);
      } else {
        setCities([]);
      }
    } else {
      fetchAllCities();
    }
  }, [selectedTalukId, useCityFilter]);

  const handleCityToggle = (cityId: number) => {
    if (selectedCityIds.includes(cityId)) {
      setSelectedCityIds(selectedCityIds.filter(id => id !== cityId));
    } else {
      setSelectedCityIds([...selectedCityIds, cityId]);
    }
  };

  const handleRemoveCity = (cityId: number) => {
    setSelectedCityIds(selectedCityIds.filter(id => id !== cityId));
  };

  const selectedCities = cities.filter(city => selectedCityIds.includes(city.id));

  const filteredMembers = Array.isArray(allMembers) ? allMembers.filter((member) => {
    const matchesSearch = !memberSearchQuery || 
      member.name?.toLowerCase().includes(memberSearchQuery.toLowerCase()) ||
      member.phone?.includes(memberSearchQuery);
    const matchesType = !memberTypeFilter || member.member_type === memberTypeFilter;
    return matchesSearch && matchesType;
  }) : [];

  const selectedMembers = Array.isArray(allMembers) ? allMembers.filter(member => selectedMemberIds.includes(member.id)) : [];

  const handleMemberToggle = (memberId: number) => {
    const currentIds = selectedMemberIds || [];
    if (currentIds.includes(memberId)) {
      setSelectedMemberIds(currentIds.filter((id: number) => id !== memberId));
    } else {
      setSelectedMemberIds([...currentIds, memberId]);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-[1600px] mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Zone Management
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                Organize and manage your zones efficiently
              </p>
            </div>
            <PermissionGuard permission={["zone.create", "zone.manage"]}>
              <button
                onClick={() => handleFormOpen()}
                className="btn-primary flex items-center gap-2 px-6 py-3 shadow-lg"
              >
                <i className="fa-solid fa-plus"></i>
                <span>Create New Zone</span>
              </button>
            </PermissionGuard>
          </div>

          {/* Filters Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 dark:border-opacity-80 p-6 mb-6">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div className="flex-1 w-full">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 pr-10 bg-white dark:bg-gray-700 text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                    placeholder="Search zones..."
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
                    <i className="fa-solid fa-search"></i>
                  </span>
                </div>
              </div>
              <div className="w-full md:w-auto">
                <select
                  value={zoneFilter}
                  onChange={(e) => setZoneFilter(e.target.value)}
                  className="w-full md:w-auto border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                >
                  <option value="">All Zones</option>
                  {zones.map((zone) => (
                    <option key={zone.id} value={zone.id}>
                      {zone.zone_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
              <p className="text-gray-600 dark:text-gray-300">Loading zones...</p>
            </div>
          </div>
        ) : filteredZones.length === 0 ? (
          <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex flex-col items-center gap-4">
              <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                <i className="fa-solid fa-map-location-dot text-3xl text-gray-400 dark:text-gray-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                No zones found
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Get started by creating your first zone
              </p>
              <PermissionGuard permission={["zone.create", "zone.manage"]}>
                <button
                  onClick={() => handleFormOpen()}
                  className="btn-primary mt-4 flex items-center gap-2 px-6 py-3"
                >
                  <i className="fa-solid fa-plus"></i>
                  <span>Create New Zone</span>
                </button>
              </PermissionGuard>
            </div>
          </div>
        ) : (
          /* Zones Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredZones.map((zone) => (
              <div
                key={zone.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-200 overflow-hidden"
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        {zone.zone_name}
                      </h3>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                        <i className="fa-solid fa-circle text-[6px] mr-2"></i>
                        Active
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {isSuperAdmin && (
                        <button
                          onClick={() => {
                            setViewingZone(zone);
                            setShowDownloadModal(true);
                          }}
                          className="p-2 text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                          title="Download zone members"
                        >
                          <i className="fa-solid fa-download"></i>
                        </button>
                      )}
                      <button
                        onClick={() => handleViewZone(zone)}
                        className="p-2 text-gray-600 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                        title="View zone details"
                      >
                        <i className="fa-solid fa-eye"></i>
                      </button>
                      <PermissionGuard permission={["zone.update", "zone.manage"]}>
                        <button
                          onClick={() => handleFormOpen(zone)}
                          className="p-2 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Edit zone"
                        >
                          <i className="fa-solid fa-edit"></i>
                        </button>
                      </PermissionGuard>
                      <PermissionGuard permission={["zone.delete", "zone.manage"]}>
                        <button
                          onClick={() => handleDeleteOpen(zone.id)}
                          className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete zone"
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </PermissionGuard>
                    </div>
                  </div>

                  {/* Zone Details */}
                  <div className="space-y-3">
                    <div>
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wide mb-1">
                        Area
                      </div>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {zone.zone_area || "N/A"}
                      </p>
                    </div>
                    {zone?.leader && (
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                          Leader
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-semibold">
                            {zone.leader.name?.charAt(0).toUpperCase() || "L"}
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900 dark:text-white">
                              {zone.leader.name || "N/A"}
                            </div>
                            {zone.leader.phone && (
                              <div className="text-xs text-gray-500 dark:text-gray-300">
                                {zone.leader.phone}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {zone?.zone_admins && zone.zone_admins.length > 0 && (
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                          Zone Admins ({zone.zone_admins.length})
                        </div>
                        <div className="space-y-2">
                          {zone.zone_admins.map((admin: any, index: number) => (
                            <div key={admin.id || index} className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 font-semibold text-xs">
                                {admin.name?.charAt(0).toUpperCase() || "A"}
                              </div>
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {admin.name || "N/A"}
                                </div>
                                {admin.phone && (
                                  <div className="text-xs text-gray-500 dark:text-gray-300">
                                    {admin.phone}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Members Section */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Members
                        </div>
                        <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-semibold">
                          {zone.users?.length || 0}
                        </span>
                      </div>
                      {zone.users && zone.users.length > 0 ? (
                        <div className="flex items-center gap-2">
                          <div className="flex -space-x-2">
                            {zone.users.slice(0, 4).map((member: any, index: number) => (
                              <div
                                key={member.id || index}
                                className="w-8 h-8 rounded-full border-2 border-white dark:border-gray-800 bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs font-semibold"
                                title={member.name}
                              >
                                {member.name?.charAt(0).toUpperCase() || 'M'}
                              </div>
                            ))}
                          </div>
                          {zone.users.length > 4 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                              +{zone.users.length - 4} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 dark:text-gray-500 italic">No members yet</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Zone Side Panel */}
      {openFormModal && (
        <div className="fixed inset-0 z-50 flex justify-end">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={handleFormClose}></div>

          {/* Side Panel */}
          <div className="relative w-full max-w-3xl bg-white dark:bg-gray-800 shadow-xl flex flex-col transition-transform duration-300 ease-out transform translate-x-0 h-full">
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between z-10">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formData?.id && zones.find((z) => z.id === formData.id)
                  ? "Edit Zone"
                  : "Create Zone"}
              </h2>
              <button 
                onClick={handleFormClose} 
                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                aria-label="Close"
              >
                <i className="fa-solid fa-times text-xl"></i>
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <form className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Zone Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData?.zone_name || ""}
                    onChange={(e: any) =>
                      handleFormChange("zone_name", e.target.value)
                    }
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                    placeholder="Enter zone name"
                    maxLength={25}
                  />
                  {formData?.zone_name?.length > 25 && (
                    <p className="text-red-500 text-xs mt-1">
                      Character limit exceeded (25 max)
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Area (Eg, Area 1, Area 2, Area 3)
                  </label>
                  <input
                    type="text"
                    value={formData?.area || ""}
                    onChange={(e: any) => handleFormChange("area", e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                    placeholder="Enter area"
                    maxLength={250}
                  />
                  {formData?.area?.length > 250 && (
                    <p className="text-red-500 text-xs mt-1">
                      Character limit exceeded (250 max)
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Zone Leader
                  </label>
                  <select
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                    value={formData?.leader || ""}
                    onChange={(e: any) => handleFormChange("leader", e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">Select a leader</option>
                    {members.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name} {member.phone ? `(${member.phone})` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative" ref={zoneAdminDropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Zone Admins
                  </label>
                  <div
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 cursor-pointer flex items-center justify-between min-h-[42px]"
                    onClick={() => setShowZoneAdminDropdown(!showZoneAdminDropdown)}
                  >
                    <div className="flex flex-wrap gap-2 flex-1">
                      {selectedZoneAdmins.length > 0 ? (
                        selectedZoneAdmins.map((admin) => (
                          <span
                            key={admin.id}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-md text-sm"
                          >
                            {admin.name}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleZoneAdminToggle(admin.id);
                              }}
                              className="hover:text-blue-600 dark:hover:text-blue-200"
                            >
                              <i className="fa-solid fa-times text-xs"></i>
                            </button>
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">Select zone admins</span>
                      )}
                    </div>
                    <i className={`fa-solid fa-chevron-${showZoneAdminDropdown ? "up" : "down"} text-gray-400 dark:text-gray-500`}></i>
                  </div>
                  {showZoneAdminDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      <div className="p-2">
                        <input
                          type="text"
                          className="w-full px-3 py-2 mb-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white"
                          placeholder="Search members..."
                          value={zoneAdminSearchQuery}
                          onChange={(e) => setZoneAdminSearchQuery(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <select
                          className="w-full px-3 py-2 mb-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white"
                          value={zoneAdminMemberTypeFilter}
                          onChange={(e) => setZoneAdminMemberTypeFilter(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">All Member Types</option>
                          {memberTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                        {filteredUsersForZoneAdmin.length > 0 ? (
                          <div className="space-y-1">
                            {filteredUsersForZoneAdmin.map((user) => {
                              const isSelected = formData?.zone_admin_ids?.includes(user.id);
                              return (
                                <label
                                  key={user.id}
                                  className="flex items-center px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleZoneAdminToggle(user.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="mr-3 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                  />
                                  <div className="flex-1">
                                    <div className="text-sm text-gray-900 dark:text-gray-100">
                                      {user.name}
                                    </div>
                                    {user.member_type && (
                                      <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                        {user.member_type.replace("-", " ")}
                                      </div>
                                    )}
                                    {user.phone && (
                                      <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {user.phone}
                                      </div>
                                    )}
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                            No members found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Members Section */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Members
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowMemberModal(true)}
                      className="btn-primary flex items-center gap-2 px-4 py-2 text-sm"
                    >
                      <i className="fa-solid fa-plus"></i>
                      <span>Add Member</span>
                    </button>
                  </div>

                  {selectedMembers.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/30 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                      <i className="fa-solid fa-users text-4xl text-gray-400 dark:text-gray-600 mb-3"></i>
                      <p className="text-gray-500 dark:text-gray-400 font-medium">No members added yet</p>
                      <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Click "Add Member" to get started</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {selectedMembers.map((member) => (
                        <div key={member.id} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400 font-semibold">
                              {member.name?.charAt(0).toUpperCase() || "M"}
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-gray-900 dark:text-white">{member.name}</div>
                              {member.phone && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">{member.phone}</div>
                              )}
                              {member.member_type && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                  {member.member_type.replace("-", " ")}
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleMemberToggle(member.id)}
                            className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Localities Section */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Localities
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowCityModal(true)}
                      className="btn-primary flex items-center gap-2 px-4 py-2 text-sm"
                    >
                      <i className="fa-solid fa-plus"></i>
                      <span>Add Locality</span>
                    </button>
                  </div>

                  {selectedCities.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/30 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                      <i className="fa-solid fa-map-marker-alt text-4xl text-gray-400 dark:text-gray-600 mb-3"></i>
                      <p className="text-gray-500 dark:text-gray-400 font-medium">No localities added yet</p>
                      <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Click "Add Locality" to get started</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {selectedCities.map((city) => (
                        <div key={city.id} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400 font-semibold">
                              <i className="fa-solid fa-map-marker-alt"></i>
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-gray-900 dark:text-white">{city.name}</div>
                              {city.code && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">Code: {city.code}</div>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveCity(city.id)}
                            className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 z-10">
              <button
                onClick={handleFormClose}
                className="btn-primary px-6 py-2.5"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="btn-primary px-6 py-2.5"
              >
                {formData?.id && zones.find((z) => z.id === formData.id)
                  ? "Update Zone"
                  : "Create Zone"}
              </button>
            </div>
          </div>

          {/* Member Selection Modal */}
          {showMemberModal && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-2xl shadow-2xl border border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Add Members</h3>
                  <button 
                    onClick={() => {
                      setShowMemberModal(false);
                      setMemberSearchQuery("");
                      setMemberTypeFilter("");
                    }} 
                    className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  >
                    <i className="fa-solid fa-times text-xl"></i>
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Search members..."
                  value={memberSearchQuery}
                  onChange={e => setMemberSearchQuery(e.target.value)}
                  className="w-full mb-4 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                />
                <select
                  className="w-full mb-4 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                  value={memberTypeFilter}
                  onChange={(e) => setMemberTypeFilter(e.target.value)}
                >
                  <option value="">All Member Types</option>
                  {memberTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <div className="max-h-96 overflow-y-auto mb-4">
                  {filteredMembers.length === 0 && (
                    <div className="text-neutral-500 text-sm">No members found.</div>
                  )}
                  <div className="grid gap-2">
                    {filteredMembers.map(member => (
                      <label key={member.id} className="flex items-center gap-3 p-3 border dark:border-neutral-700 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedMemberIds.includes(member.id)}
                          onChange={() => handleMemberToggle(member.id)}
                        />
                        <div className="flex items-center gap-3 flex-1">
                          <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400 font-semibold">
                            {member.name?.charAt(0).toUpperCase() || "M"}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-black dark:text-white">{member.name}</div>
                            {member.member_type && (
                              <div className="text-sm text-neutral-500 dark:text-neutral-400 capitalize">
                                {member.member_type.replace("-", " ")}
                              </div>
                            )}
                            {member.phone && (
                              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                                {member.phone}
                              </div>
                            )}
                            {member.zone && (
                              <div className="text-xs text-orange-500 dark:text-orange-400">
                                Current Zone: {member.zone.zone_name}
                              </div>
                            )}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowMemberModal(false);
                      setMemberSearchQuery("");
                      setMemberTypeFilter("");
                    }}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowMemberModal(false);
                      setMemberSearchQuery("");
                      setMemberTypeFilter("");
                    }}
                    className="btn-primary px-4 py-2 flex items-center gap-2"
                  >
                    <i className="fa-solid fa-check"></i>
                    <span>Done</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* City Selection Modal */}
          {showCityModal && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-2xl shadow-2xl border border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Add Localities</h3>
                  <button 
                    onClick={() => {
                      setShowCityModal(false);
                      setCitySearchQuery("");
                      setUseCityFilter(false);
                      setSelectedCountryId(null);
                      setSelectedStateId(null);
                      setSelectedDistrictId(null);
                      setSelectedTalukId(null);
                      setStates([]);
                      setDistricts([]);
                      setTaluks([]);
                      fetchAllCities();
                    }} 
                    className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  >
                    <i className="fa-solid fa-times text-xl"></i>
                  </button>
                </div>
                
                {/* Filter Checkbox */}
                <div className="mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useCityFilter}
                      onChange={(e) => {
                        setUseCityFilter(e.target.checked);
                        if (!e.target.checked) {
                          // Reset filters when unchecked
                          setSelectedCountryId(null);
                          setSelectedStateId(null);
                          setSelectedDistrictId(null);
                          setSelectedTalukId(null);
                          setStates([]);
                          setDistricts([]);
                          setTaluks([]);
                          fetchAllCities();
                        }
                      }}
                      className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 accent-gray-900 dark:accent-gray-400"
                    />
                    <span className="text-gray-700 dark:text-gray-300 font-medium">
                      Filter by location to find locality
                    </span>
                  </label>
                </div>

                {/* Filter Dropdowns - Only shown when checkbox is checked */}
                {useCityFilter && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Country
                      </label>
                      <select
                        value={selectedCountryId || ""}
                        onChange={(e: any) => {
                          const countryId = e.target.value ? Number(e.target.value) : null;
                          setSelectedCountryId(countryId);
                          setSelectedStateId(null);
                          setSelectedDistrictId(null);
                          setSelectedTalukId(null);
                        }}
                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                      >
                        <option value="">Select Country</option>
                        {countries.map((country) => (
                          <option key={country.id} value={country.id}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        State
                      </label>
                      <select
                        value={selectedStateId || ""}
                        onChange={(e: any) => {
                          const stateId = e.target.value ? Number(e.target.value) : null;
                          setSelectedStateId(stateId);
                          setSelectedDistrictId(null);
                          setSelectedTalukId(null);
                        }}
                        disabled={!selectedCountryId}
                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="">Select State</option>
                        {states.map((state) => (
                          <option key={state.id} value={state.id}>
                            {state.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        District
                      </label>
                      <select
                        value={selectedDistrictId || ""}
                        onChange={(e: any) => {
                          const districtId = e.target.value ? Number(e.target.value) : null;
                          setSelectedDistrictId(districtId);
                          setSelectedTalukId(null);
                        }}
                        disabled={!selectedStateId}
                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="">Select District</option>
                        {districts.map((district) => (
                          <option key={district.id} value={district.id}>
                            {district.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Taluk
                      </label>
                      <select
                        value={selectedTalukId || ""}
                        onChange={(e: any) => {
                          const talukId = e.target.value ? Number(e.target.value) : null;
                          setSelectedTalukId(talukId);
                        }}
                        disabled={!selectedDistrictId}
                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="">Select Taluk</option>
                        {taluks.map((taluk) => (
                          <option key={taluk.id} value={taluk.id}>
                            {taluk.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <input
                  type="text"
                  placeholder="Search localities..."
                  value={citySearchQuery}
                  onChange={e => setCitySearchQuery(e.target.value)}
                  className="w-full mb-4 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
                />
                <div className="max-h-96 overflow-y-auto mb-4">
                  {filteredCities.length === 0 && (
                    <div className="text-neutral-500 text-sm">No localities found.</div>
                  )}
                  <div className="grid gap-2">
                    {filteredCities.map(city => (
                      <label key={city.id} className="flex items-center gap-3 p-3 border dark:border-neutral-700 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedCityIds.includes(city.id)}
                          onChange={() => handleCityToggle(city.id)}
                        />
                        <div className="flex items-center gap-3 flex-1">
                          <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400">
                            <i className="fa-solid fa-map-marker-alt"></i>
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-black dark:text-white">{city.name}</div>
                            {city.code && (
                              <div className="text-sm text-neutral-500 dark:text-neutral-400">Code: {city.code}</div>
                            )}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCityModal(false);
                      setCitySearchQuery("");
                      setUseCityFilter(false);
                      setSelectedCountryId(null);
                      setSelectedStateId(null);
                      setSelectedDistrictId(null);
                      setSelectedTalukId(null);
                      setStates([]);
                      setDistricts([]);
                      setTaluks([]);
                      fetchAllCities();
                    }}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCityModal(false);
                      setCitySearchQuery("");
                      setUseCityFilter(false);
                      setSelectedCountryId(null);
                      setSelectedStateId(null);
                      setSelectedDistrictId(null);
                      setSelectedTalukId(null);
                      setStates([]);
                      setDistricts([]);
                      setTaluks([]);
                      fetchAllCities();
                    }}
                    className="btn-primary px-4 py-2 flex items-center gap-2"
                  >
                    <i className="fa-solid fa-check"></i>
                    <span>Done</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* View Zone Modal */}
      {viewZoneModal && viewingZone && (
        <div className="fixed inset-0 z-50 flex justify-end">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={handleCloseViewModal}></div>

          {/* Side Panel */}
          <div className="relative w-full max-w-3xl bg-white dark:bg-gray-800 shadow-xl flex flex-col transition-transform duration-300 ease-out transform translate-x-0 h-full">
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between z-10">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Zone Details
              </h2>
              <button 
                onClick={handleCloseViewModal} 
                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                aria-label="Close"
              >
                <i className="fa-solid fa-times text-xl"></i>
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Zone Name and Status */}
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                    {viewingZone.zone_name}
                  </h3>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                      <i className="fa-solid fa-circle text-[6px] mr-2"></i>
                      Active
                    </span>
                  </div>
                </div>

                {/* Zone Area */}
                {viewingZone.zone_area && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Area
                    </label>
                    <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
                      <p className="text-base text-gray-900 dark:text-white">
                        {viewingZone.zone_area}
                      </p>
                    </div>
                  </div>
                )}

                {/* Zone Leader Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Zone Leader
                  </label>
                  {viewingZone?.leader ? (
                    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-semibold">
                          {viewingZone.leader.name?.charAt(0).toUpperCase() || "L"}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            {viewingZone.leader.name || "N/A"}
                          </div>
                          {viewingZone.leader.phone && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {viewingZone.leader.phone}
                            </div>
                          )}
                          {viewingZone.leader.member_type && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 capitalize mt-1">
                              {viewingZone.leader.member_type.replace("-", " ")}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
                      <p className="text-sm text-gray-500 dark:text-gray-400">No leader assigned</p>
                    </div>
                  )}
                </div>

                {/* Zone Admins Section */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Zone Admins
                    </label>
                    {viewingZone?.zone_admins && viewingZone.zone_admins.length > 0 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {viewingZone.zone_admins.length} {viewingZone.zone_admins.length === 1 ? 'admin' : 'admins'}
                      </span>
                    )}
                  </div>
                  {viewingZone?.zone_admins && viewingZone.zone_admins.length > 0 ? (
                    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="space-y-2">
                        {viewingZone.zone_admins.map((admin: any, index: number) => (
                          <div key={admin.id || index} className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 font-semibold text-xs">
                              {admin.name?.charAt(0).toUpperCase() || "A"}
                            </div>
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {admin.name || "N/A"}
                              </div>
                              {admin.phone && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {admin.phone}
                                </div>
                              )}
                              {admin.member_type && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                  {admin.member_type.replace("-", " ")}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
                      <p className="text-sm text-gray-500 dark:text-gray-400">No zone admins assigned</p>
                    </div>
                  )}
                </div>

                {/* Localities Section */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Localities
                    </label>
                    {viewingZone?.cities && viewingZone.cities.length > 0 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {viewingZone.cities.length} {viewingZone.cities.length === 1 ? 'locality' : 'localities'}
                      </span>
                    )}
                  </div>
                  {viewingZone?.cities && viewingZone.cities.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                      {viewingZone.cities.map((city: any, index: number) => (
                        <div key={city.id || index} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300">
                              <i className="fa-solid fa-map-marker-alt text-xs"></i>
                            </div>
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {city.name}
                              </div>
                              {city.code && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  Code: {city.code}
                                </div>
                              )}
                              {city.taluk?.district?.state?.name && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {city.taluk.district.state.name} → {city.taluk.district.name} → {city.taluk.name}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
                      <p className="text-sm text-gray-500 dark:text-gray-400">No localities added yet</p>
                    </div>
                  )}
                </div>

                {/* Members Section */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Members
                    </label>
                    {viewingZone?.users && viewingZone.users.length > 0 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {viewingZone.users.length} {viewingZone.users.length === 1 ? 'member' : 'members'}
                      </span>
                    )}
                  </div>
                  {viewingZone?.users && viewingZone.users.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {viewingZone.users.map((member: any, index: number) => (
                        <div key={member.id || index} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 font-semibold text-xs">
                              {member.name?.charAt(0).toUpperCase() || "M"}
                            </div>
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {member.name || "N/A"}
                              </div>
                              {member.phone && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {member.phone}
                                </div>
                              )}
                              {member.member_type && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                                  {member.member_type.replace("-", " ")}
                                </div>
                              )}
                              {member.city && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {member.city.name}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
                      <p className="text-sm text-gray-500 dark:text-gray-400">No members assigned to this zone</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end z-10">
              <button
                onClick={handleCloseViewModal}
                className="btn-primary px-6 py-2.5"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Download Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-2xl shadow-2xl border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Confirm Export Download</h3>
              <button 
                onClick={() => setShowDownloadModal(false)} 
                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                <i className="fa-solid fa-times text-xl"></i>
              </button>
            </div>
            
            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700/30 cursor-not-allowed opacity-75">
                  <input
                    type="checkbox"
                    checked={selectedFields.zoneName}
                    disabled
                    className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Zone Name</span>
                </label>
                
                <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700/30 cursor-not-allowed opacity-75">
                  <input
                    type="checkbox"
                    checked={selectedFields.memberName}
                    disabled
                    className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Member Name</span>
                </label>
                
                <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700/30 cursor-not-allowed opacity-75">
                  <input
                    type="checkbox"
                    checked={selectedFields.phone}
                    disabled
                    className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Phone</span>
                </label>
                
                <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700/30 cursor-not-allowed opacity-75">
                  <input
                    type="checkbox"
                    checked={selectedFields.city}
                    disabled
                    className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Locality</span>
                </label>
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDownloadModal(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  handleDownloadExcel();
                  setShowDownloadModal(false);
                  setViewingZone(null);
                }}
                className="btn-primary px-4 py-2 flex items-center gap-2"
              >
                <i className="fa-solid fa-download"></i>
                <span>Download Excel</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {openDeleteModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={handleDeleteClose}
        >
          <div 
            ref={deleteModalRef} 
            className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Confirm Delete
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Are you sure you want to delete this zone?
            </p>
            <div className="flex justify-end gap-3">
              <button 
                onClick={handleDeleteClose}
                className="btn-outline"
              >
                Cancel
              </button>
              <button 
                onClick={handleDelete}
                className="btn-danger"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZoneList;
